import { APIResponseError } from '$lib/error';
import type ERAR from './spec';

type Endpoints = typeof ERAR.Endpoints;
export type EndpointKey = keyof Endpoints;
export type RequestOf<T extends EndpointKey> =
  Endpoints[T] extends ERAR.Endpoint<infer R, any>
    ? OmittedOptionalFields<
        R &
          RequestInit & {
            params: { [key in ParametersOf<T>]: string | number };
            fetch: Fetch;
          }
      >
    : never;
export type ResultOf<T extends EndpointKey> =
  Endpoints[T] extends ERAR.Endpoint<any, infer R> ? R : never;
type ParametersOf<T extends string> = T extends `${string}[${infer Name}]${infer Rest}`
  ? Name | ParametersOf<Rest>
  : never;
type Omittable<T> = keyof {
  [key in keyof T as undefined extends T[key] ? never : key]: true;
} extends never
  ? true
  : false;
type OmittedOptionalFields<T> = {
  [key in keyof T as Omittable<T[key]> extends true ? key : never]?: T[key];
} & {
  [key in keyof T as Omittable<T[key]> extends false ? key : never]: T[key];
};
export type Fetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
type CallAPIParameters<T extends EndpointKey> =
  Omittable<RequestOf<T>> extends true
    ? [name: T, options?: RequestOf<T>]
    : [name: T, options: RequestOf<T>];

export async function api<TKey extends EndpointKey>(
  ...[key, options]: CallAPIParameters<TKey>
): Promise<ResultOf<TKey>> {
  const [method, addressWithPlaceholder] = key.split(' ');
  const {
    query,
    params,
    body,
    fetch: customFetch,
    ...restOptions
  } = (options ?? {}) as {
    query?: Record<string, string>;
    params?: Record<string, string>;
    fetch?: Fetch;
    body?: Record<string, string>;
  } & Record<string, string>;

  let address: string = addressWithPlaceholder;
  if (params) {
    for (const [param, value] of Object.entries(params)) {
      address = addressWithPlaceholder.replaceAll(`[${param}]`, value);
    }
  }

  const searchParams = new URLSearchParams();
  if (query) {
    for (const [queryKey, value] of Object.entries(query)) {
      if (value === undefined) continue;
      searchParams.set(queryKey, value);
    }
  }

  const result = await (customFetch ?? fetch)(
    `/erar${address}${query ? `?${searchParams.toString()}` : ''}`,
    {
      method,
      body: body ? JSON.stringify(body) : undefined,
      ...restOptions,
    },
  );

  if (!result.ok) {
    throw new APIResponseError(result);
  }

  if (result.headers.get('Content-Type')?.includes('application/json')) {
    return await result.json();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return {} as any;
}
